import { useCallback, useEffect, useState } from 'react';
import { redeemPromo } from './redeem-promo';
import { promoFormMessage, type PromoFormMessage } from './promo-form';
import { TERMINAL_PROMO_OUTCOMES } from './promo-outcome';
import { CREDITS_REFRESH_EVENT } from './use-credits';
import { blackFridayPromoCode, useBlackFridayTestCode } from './black-friday-dev-code';

export interface BlackFridayRedeem {
  busy: boolean;
  /** The service's own answer, when it refused or failed. Null while the CTA is still worth pressing. */
  message: PromoFormMessage | null;
  /** Whether the CTA still does anything — a refusal is final, only "no verdict came back" is retryable. */
  canPress: boolean;
  /** The service granted the window. The modal waits for this before it closes. */
  redeemed: boolean;
  redeem: () => void;
}

/**
 * One redemption of `blackfriday2026`, for the band's CTA and the modal's.
 *
 * It is the SAME call the `/promo/<code>` campaign link and the Settings "Redeem a code" form make, so
 * there is one redemption rule rather than three — and the same outcome → copy mapping, so a refusal
 * reads identically wherever the user met the offer.
 *
 * A redemption answers OK with an outcome and never throws, which is what makes every refusal a sentence
 * to show rather than a failure to swallow. The CTA goes once a verdict is in: an unknown, expired or
 * used-up code will not become valid on a second press. `retry` and `failed` are the exception — nothing
 * was decided, so pressing again is the right thing.
 *
 * On success nothing here flips the band. The credits refresh does: the overview comes back carrying
 * `freeUsageUntil`, and the band resolves to the countdown off that one source of truth, so the screen
 * can never claim a window the account does not have.
 *
 * The code is read at press time, so Dev Tools ▸ Black Friday can point the CTA at a test code. A
 * switch also clears the verdict: "unknown code" for one code says nothing about the next.
 */
export function useBlackFridayRedeem(source: string): BlackFridayRedeem {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<PromoFormMessage | null>(null);
  const [settled, setSettled] = useState(false);
  const [redeemed, setRedeemed] = useState(false);
  const testCode = useBlackFridayTestCode();
  useEffect(() => {
    setMessage(null);
    setSettled(false);
    setRedeemed(false);
  }, [testCode]);

  const redeem = useCallback(() => {
    if (busy || settled) return;
    setBusy(true);
    void redeemPromo(blackFridayPromoCode(), source).then((result) => {
      setBusy(false);
      if (result.outcome === 'redeemed') {
        setSettled(true);
        setRedeemed(true);
        window.dispatchEvent(new Event(CREDITS_REFRESH_EVENT));
        return;
      }
      setMessage(promoFormMessage(result.outcome, result.credits, result.freeHours));
      setSettled(TERMINAL_PROMO_OUTCOMES.has(result.outcome));
    });
  }, [busy, settled, source]);

  return { busy, message, canPress: !settled, redeemed, redeem };
}
